import type { ActorTransaction } from "@-/dev.improve.haniverse.database.actor";
import { createOrReviveActorRow } from "@-/dev.improve.haniverse.database.actor/create-or-revive";
import { deriveUserSlideId } from "@-/dev.improve.haniverse.database.schemas.actor.identity";
import { actorSchema } from "@-/dev.improve.haniverse.database.schemas.actor.schema";

/**
 * Make the `user_slide` row of the slide with catalogue UUID `key` exist and be live, and return
 * its id. A live row is kept as it is; a missing or tombstoned one is created or brought back
 * blank: neither completed nor favorited.
 *
 * The id is derived from `key`, so two devices that touch the slide before they sync write the
 * same row. Set `completedAt` / `favoritedAt` with an UPDATE afterwards: each then reaches the
 * cloud as a change to that one column, and one device's completion cannot erase the other's
 * favorite.
 */
export async function ensureUserSlideRow(
  tx: ActorTransaction,
  key: string,
  nowIso: string,
): Promise<string> {
  const existing = await tx.query.userSlide.findFirst({ columns: { id: true }, where: { key } });
  if (existing !== undefined) {
    return existing.id;
  }
  const id = deriveUserSlideId(key);
  await createOrReviveActorRow(tx, actorSchema.userSlide, { createdAt: nowIso, id, key });
  return id;
}
